'use client';

import { useState, useCallback, lazy, Suspense } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import type { ShareCardData } from '@/lib/share-card-renderer';
import { ImageIcon } from './share-icons';
import { BTN } from './SocialShareButtons';

const ShareCard = lazy(() =>
  import('./ShareCard').then((m) => ({ default: m.ShareCard }))
);

interface ShareImageButtonProps {
  title: string;
  subtitle?: string;
  cardData?: Partial<ShareCardData>;
  inMenu?: boolean;
}

export function ShareImageButton({
  title,
  subtitle,
  cardData,
  inMenu,
}: ShareImageButtonProps) {
  const lang = useCalendarStore((s) => s.lang);
  const [cardOpen, setCardOpen] = useState(false);

  const handleShareImage = useCallback(() => {
    setCardOpen(true);
    window._paq?.push(['trackEvent', 'Share', 'ImageOpen', title]);
  }, [title]);

  const labelImage = uiFb('ui.share_image', 'Share as image', lang);
  const shareCardData: ShareCardData = { title, subtitle, ...cardData };
  const roleProps = inMenu ? { role: 'menuitem' as const } : {};

  return (
    <>
      <button
        onClick={handleShareImage}
        className={BTN}
        aria-label={labelImage}
        title={labelImage}
        {...roleProps}
      >
        <ImageIcon />
      </button>
      {cardOpen && (
        <Suspense fallback={null}>
          <ShareCard
            data={shareCardData}
            open={cardOpen}
            onClose={() => setCardOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
}
